import { useEffect, useRef, useState } from 'react';
import { useShowPopup, useInitData } from '@vkruglikov/react-telegram-web-app';
import { useProxyLocalStorage } from '../v2/ProxyLocalStorage';
import { getTonBalance } from '../config/ton';
import { getOkxLanguage } from '../v2/utils/utils';
import { ConnectResult } from '../TomoContext';
import { Locales, ConnectedWallet } from '@okxconnect/ui';
import {
  OKX_CONNECT_ERROR_CODES,
  OKXConnectError,
} from '@okxconnect/core/src/protocol/error/okx-connect.error';
import {
  THEME,
  OKX_UI_CONNECTION_AND_TRANSACTION_EVENT,
} from '../config/okxConnect';

let okxTonConnect;
interface Option {
  connectSuccess?: () => void;
  connectError?: () => void;
  metaData?: OkxConnectMeta;
  setProviders: (providers: Record<string, any>) => void;
  chain?: string;
  theme?: 'dark' | 'light';
  setConnectResult?: (result: ConnectResult) => void;
}

interface OkxConnectMeta {
  name: string;
  icon: string;
}

const useOkxTonConnect = (option?: Option) => {
  const {
    connectSuccess,
    connectError,
    metaData = { name: 'dapp', icon: ' ' },
    setProviders,
    chain,
    theme,
    setConnectResult,
  } = option || {};
  const okxConnectRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const showPopup = useShowPopup();
  const [initDataUnsafe] = useInitData();

  const storage = useProxyLocalStorage();

  const initialProvider = async () => {
    let language = getOkxLanguage(initDataUnsafe?.user?.language_code);
    if (chain === 'TON') {
      if (okxTonConnect) okxConnectRef.current = okxTonConnect;
      else {
        const OKXTonConnectUI = (await import('@okxconnect/ui'))
          .OKXTonConnectUI;
        okxTonConnect = new OKXTonConnectUI({
          dappMetaData: metaData,
          actionsConfiguration: {
            modals: 'all',
            returnStrategy: 'tg://resolve',
          },
          uiPreferences: {
            theme: theme === 'dark' ? THEME.DARK : THEME.LIGHT,
          },
          language: language as Locales,
          restoreConnection: true,
        });
        okxConnectRef.current = okxTonConnect;
        listenStatusChange();
      }
    }
    return okxTonConnect.current;
  };

  const handleConnected = async () => {
    setConnected(true);
    await initialProvider();
    const okxTonConnect = okxConnectRef.current;
    const account = okxTonConnect.account;
    storage.set('accounts', {
      ...(storage.get('accounts') || {}),
      ton: account,
    });
    connectSuccess && connectSuccess();
    storage.set('connect_type', 'OKX');
    Object.defineProperty(okxTonConnect, 'getBalance', {
      value: async () => getTonBalance({ tonAddress: account.address }),
      writable: true,
      enumerable: true,
      configurable: false,
    });
    window.tomo_ton = okxTonConnect;
    setProviders(pre => {
      return {
        ...pre,
        tomo_ton: window.tomo_ton,
      };
    });
  };

  const handleDisconnected = () => {
    setConnected(false);
    setProviders(pre => ({ ...pre, tomo_ton: null }));
    storage.set('accounts', {
      ...(storage.get('accounts') || {}),
      ton: undefined,
    });
    window.tomo_ton = null;
    storage.set('connect_type', '');
  };

  const connectType = storage.get('connect_type');

  useEffect(() => {
    if (chain === 'TON' && okxConnectRef.current?.connected) {
      handleConnected();
    } else if (
      chain === 'TON' &&
      connectType === 'OKX' &&
      !okxConnectRef.current?.connected
    ) {
      handleDisconnected();
    }
  }, [chain, okxConnectRef.current?.connected]);

  useEffect(() => {
    setConnected(okxConnectRef.current?.connected);
  }, [okxConnectRef.current?.connected]);

  const listenStatusChange = () => {
    const okxTonConnect = okxConnectRef.current;
    const unsubscribe = okxTonConnect.onStatusChange(
      (walletInfo: ConnectedWallet | null) => {
        if (!walletInfo) {
          setConnected(false);
          setProviders(pre => ({ ...pre, tomo_ton: null }));
          storage.set('accounts', {
            ...(storage.get('accounts') || {}),
            ton: undefined,
          });
        } else if (
          walletInfo?.connectItems?.tonProof &&
          'proof' in walletInfo?.connectItems.tonProof
        ) {
          setConnectResult({
            result: { tonProof: walletInfo.connectItems.tonProof },
          });
        }
      },
      (err: OKXConnectError) => {
        if (err.code === OKX_CONNECT_ERROR_CODES.USER_REJECTS_ERROR) {
          console.log('user reject connect', err);
        } else {
          console.log('Connection status err:', err);
        }
      }
    );
    if (chain !== 'TON') unsubscribe();
  };

  /** add okx ton connect event listener */
  useEffect(() => {
    function onSuccess(event) {
      if (event instanceof CustomEvent) {
        handleConnected();
      }
    }

    function onError(event) {
      if (event instanceof CustomEvent) {
        connectError && connectError();
        setConnected(false);
        storage.set('connect_type', '');
        showPopup({ message: 'Failed to connect' });
      }
    }

    window.addEventListener(
      OKX_UI_CONNECTION_AND_TRANSACTION_EVENT.OKX_UI_CONNECTION_COMPLETED,
      onSuccess
    );

    window.addEventListener(
      OKX_UI_CONNECTION_AND_TRANSACTION_EVENT.OKX_UI_CONNECTION_ERROR,
      onError
    );

    return () => {
      window.removeEventListener(
        OKX_UI_CONNECTION_AND_TRANSACTION_EVENT.OKX_UI_CONNECTION_COMPLETED,
        onSuccess
      );
      window.removeEventListener(
        OKX_UI_CONNECTION_AND_TRANSACTION_EVENT.OKX_UI_CONNECTION_ERROR,
        onError
      );
    };
  }, []);

  /** connect wallet */
  const connect = async (connectOption?: {
    redirect?: string;
    tonProof?: string;
  }) => {
    await initialProvider();
    const okxTonConnect = okxConnectRef.current;
    if (okxTonConnect?.connected) {
      okxTonConnect?.disconnect();
    }
    connectOption?.tonProof &&
      okxTonConnect.setConnectRequestParameters({
        state: 'ready',
        value: { tonProof: connectOption.tonProof },
      });
    okxTonConnect.openModal();
    return true;
  };

  const disconnect = async () => {
    const okxTonConnect = okxConnectRef.current;
    try {
      setConnected(false);
      setProviders(pre => ({ ...pre, tomo_ton: null }));
      storage.set('accounts', {
        ...(storage.get('accounts') || {}),
        ton: undefined,
      });
      window.tomo_ton = null;
      okxTonConnect.disconnect();
    } catch (error) {
      if (error instanceof OKXConnectError) {
        switch (error.code) {
          case OKX_CONNECT_ERROR_CODES.NOT_CONNECTED_ERROR:
            alert('Not connected');
            break;
          default:
            alert('Unknown error happened');
            break;
        }
      } else {
        alert('Unknown error happened');
      }
    }
  };

  const sendTransaction = async transactionRequest => {
    await initialProvider();
    const okxTonConnect = okxConnectRef.current;
    // useage param
    const oTransactionRequest = transactionRequest || {
      messages: [
        {
          // address: "0:cd60b11355dbc5ded0d14e245fef1ba3014154ef077959e8102e10656d6ea02c", // destination address
          address:
            '0:94f90fe21c344f76f28f75a20a15746e004149723711639691516dc7d00025e8',
          amount: '20000000', //Toncoin in nanotons
        },
      ],
      validUntil: Date.now() / 1000 + 360,
    };

    try {
      const result = await okxTonConnect.sendTransaction(oTransactionRequest);
      return result;
    } catch (error) {
      if (error instanceof OKXConnectError) {
        switch (error.code) {
          case OKX_CONNECT_ERROR_CODES.USER_REJECTS_ERROR:
            alert('You rejected the transaction.');
            break;
          case OKX_CONNECT_ERROR_CODES.NOT_CONNECTED_ERROR:
            alert('Not connected');
            break;
          default:
            alert('Unknown error happened');
            break;
        }
      } else {
        alert('Unknown error happened');
      }
    }
  };

  return {
    okxConnect: okxConnectRef.current,
    connect,
    disconnect,
    connected,
    sendTransaction,
  };
};

export default useOkxTonConnect;
